import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../utils/api';
import { TenantSettings } from '../types';

export const useTenantSettings = () => {
  return useQuery<TenantSettings>({
    queryKey: ['tenant', 'settings'],
    queryFn: () => api('/tenant/settings'),
  });
};

export const useUpdateTenantSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<TenantSettings>) =>
      api('/tenant/settings', { method: 'PUT', body: data }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant'] });
    },
  });
};

/** Кто пользуется разделами: раздел → роли (пусто — все). Только `company.settings`. */
export interface ModuleAccessView {
  modules: string[];
  access: Record<string, string[]>;
  roles: { _id: string; name: string }[];
  isOwner?: boolean;
  /** Роли смотрящего: закрыть раздел себе — только с подтверждением. */
  myRoles?: string[];
}

export const useModuleAccess = (isEnabled = true) =>
  useQuery<ModuleAccessView>({
    queryKey: ['tenant', 'module-access'],
    queryFn: () => api('/tenant/module-access'),
    enabled: isEnabled,
  });

export const useSaveModuleAccess = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ module, roleIds }: { module: string; roleIds: string[] }) =>
      api(`/tenant/module-access/${module}`, { method: 'PUT', body: { roleIds } }),
    // Сразу показываем выбор, не дожидаясь перезапроса; при ошибке перезапрос вернёт сервер.
    onMutate: ({ module, roleIds }) =>
      queryClient.setQueryData<ModuleAccessView>(['tenant', 'module-access'], (prev) =>
        prev ? { ...prev, access: { ...prev.access, [module]: roleIds } } : prev,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant', 'module-access'] });
      // Свои разделы могли измениться — меню берёт их из прав.
      queryClient.invalidateQueries({ queryKey: ['users', 'me', 'permissions'] });
    },
  });
};
